import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, UploadCloud, RefreshCw, FileAudio, Repeat, Sparkles, X, Pencil, Save, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { DraftActions } from "@/components/DraftActions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACCEPT = ".mp3,.m4a,.wav,.mp4";
const MAX_MB = 50;

export default function RecreateKonten({ onSendToCalendar }) {
  const [profiles, setProfiles] = useState([]);
  const [identityId, setIdentityId] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | uploading | transcribing | done
  const [transcript, setTranscript] = useState("");
  const [topic, setTopic] = useState("");
  const [recreating, setRecreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const topicRef = useRef(null);

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  useEffect(() => {
    api.get("/identities").then((r) => { setProfiles(r.data); if (r.data.length > 0) setIdentityId(r.data[0].id); }).catch(() => {});
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (![".mp3", ".m4a", ".wav", ".mp4"].includes(ext)) { toast.error("Format harus mp3, m4a, wav, atau mp4 ya"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File kegedean. Maksimal ${MAX_MB}MB ya`); return; }

    setFileName(file.name);
    setDraft("");
    setPhase("uploading");
    setUploadPct(0);

    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/recreate/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploadPct(pct);
          if (pct >= 100) setPhase("transcribing");
        },
      });
      setTranscript(res.data.transcript);
      setPhase("done");
      toast.success("Transkrip selesai! Cek & koreksi kalau perlu.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal memproses file. Coba lagi ya!";
      toast.error(msg);
      setPhase("idle");
      setFileName("");
    }
  };

  const recreate = async () => {
    if (!transcript.trim()) { toast.error("Transkrip masih kosong ya"); return; }
    if (!topic.trim()) { toast.error("Isi dulu topik barunya"); return; }
    setRecreating(true);
    try {
      const res = await api.post("/recreate/generate", { source_transcript: transcript, topic, identity_id: identityId || null });
      setDraft(res.data.draft);
      toast.success("Hasil recreate jadi & tersimpan ke Library Skrip!");
    } catch (e) {
      toast.error("Yah, ada gangguan pas nyambung ke AI. Coba lagi ya!");
    } finally {
      setRecreating(false);
    }
  };

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(draft); setCopied(true); toast.success("Draft disalin ke clipboard"); setTimeout(() => setCopied(false), 1800); }
    catch (e) { toast.error("Gagal menyalin — coba salin manual ya"); }
  };

  const saveToLibrary = async (text) => {
    try { await api.post("/scripts", { topic: topic || "Hasil Recreate", full_text: text, source: "recreate" }); return true; }
    catch (e) { return false; }
  };

  const copySave = async () => {
    try { await navigator.clipboard.writeText(draft); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {}
    const ok = await saveToLibrary(draft);
    toast.success(ok ? "Disalin & disimpan ke Library Skrip" : "Disalin ke clipboard");
  };

  const finishEdit = async () => {
    setEditing(false);
    const ok = await saveToLibrary(draft);
    toast.success(ok ? "Perubahan disimpan sebagai versi terbaru" : "Perubahan disimpan");
  };

  const deleteDraft = () => {
    if (!window.confirm("Hapus hasil recreate ini? Tindakan ini tidak bisa dibatalkan.")) return;
    setDraft(""); setEditing(false);
    toast.success("Hasil recreate dihapus");
  };

  const reset = () => { setFileName(""); setTranscript(""); setPhase("idle"); setUploadPct(0); setDraft(""); setTopic(""); setEditing(false); if (topicRef.current) topicRef.current.style.height = "auto"; };

  const busy = phase === "uploading" || phase === "transcribing";

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Tiru pola</p>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Recreate Konten</h1>
          <p className="text-sk-muted mt-2 max-w-xl leading-relaxed">Upload video/audio yang gaya penyampaiannya kamu suka. AI transkrip, lalu tiru polanya buat topik baru kamu.</p>
        </div>
        {profiles.length > 0 && (
          <Select value={identityId} onValueChange={setIdentityId}>
            <SelectTrigger className="w-[180px] bg-sk-surface border-sk-line rounded-xl text-sk-cream" data-testid="recreate-dna-select">
              <SelectValue placeholder="Pilih DNA brand" />
            </SelectTrigger>
            <SelectContent className="bg-sk-surface border-sk-line text-sk-cream">
              {profiles.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`recreate-dna-opt-${p.id}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: upload + transcript */}
        <div className="space-y-5">
          {/* Upload zone */}
          <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm">
            <label className="block text-sm font-semibold text-sk-cream mb-3">1 · Upload file audio/video</label>
            <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="recreate-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />

            {phase === "idle" ? (
              <button
                onClick={() => inputRef.current?.click()}
                data-testid="recreate-upload-btn"
                className="w-full rounded-2xl border-2 border-sk-line hover:border-sk-brand p-8 flex flex-col items-center justify-center gap-3 transition-colors group"
              >
                <span className="h-14 w-14 rounded-2xl bg-sk-brand/12 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <UploadCloud className="h-7 w-7 text-sk-brand" />
                </span>
                <span className="font-bold text-sk-cream">Pilih file atau klik di sini</span>
                <span className="text-xs text-sk-muted">mp3, m4a, wav, mp4 · maksimal {MAX_MB}MB</span>
              </button>
            ) : (
              <div className="rounded-2xl border border-sk-line bg-sk-bg p-5" data-testid="recreate-progress">
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-10 w-10 rounded-xl bg-sk-brand/12 flex items-center justify-center shrink-0"><FileAudio className="h-5 w-5 text-sk-brand" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-sk-cream truncate">{fileName}</p>
                    <p className="text-xs text-sk-muted">
                      {phase === "uploading" && `Mengunggah... ${uploadPct}%`}
                      {phase === "transcribing" && "Mentranskrip audio... (bisa makan waktu beberapa saat)"}
                      {phase === "done" && "Transkrip selesai"}
                    </p>
                  </div>
                  {phase === "done" && (
                    <button onClick={reset} data-testid="recreate-reset-btn" className="text-sk-muted hover:text-sk-brand p-1.5"><X className="h-4 w-4" /></button>
                  )}
                </div>
                {busy && (
                  <div className="h-2 rounded-full bg-sk-surface2 overflow-hidden">
                    {phase === "uploading" ? (
                      <div className="h-full bg-sk-brand transition-all duration-200" style={{ width: `${uploadPct}%` }} />
                    ) : (
                      <div className="h-full w-1/3 bg-sk-brand animate-pulse rounded-full" style={{ animation: "sk-glow 1.4s ease-in-out infinite" }} />
                    )}
                  </div>
                )}
                {phase === "transcribing" && (
                  <div className="flex items-center gap-2 mt-3 text-xs text-sk-brand">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" /> AI lagi dengerin & nulis transkripnya...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Transcript editor */}
          <div className={`rounded-2xl border bg-sk-surface p-6 shadow-sm ${phase === "transcribing" ? "sk-generating" : "border-sk-line"}`}>
            <label className="block text-sm font-semibold text-sk-cream mb-2">2 · Transkrip (bisa diedit)</label>
            <textarea
              data-testid="recreate-transcript"
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={9}
              placeholder="Hasil transkrip bakal muncul di sini setelah upload. Kamu bisa koreksi manual kalau ada yang salah dengar."
              className="w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent leading-relaxed"
            />
          </div>

          {/* Topic + recreate */}
          <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm">
            <label className="block text-sm font-semibold text-sk-cream mb-1">3 · Topik baru kamu</label>
            <p className="text-xs text-sk-muted mb-2.5">Buat ulang script kamu dengan pola di atas</p>
            <div className="rounded-xl border border-sk-line bg-sk-bg overflow-hidden focus-within:ring-2 focus-within:ring-sk-brand focus-within:border-transparent">
              <textarea
                ref={topicRef}
                data-testid="recreate-topic-input"
                value={topic}
                onChange={(e) => { setTopic(e.target.value); autoGrow(e.target); }}
                rows={1}
                placeholder="cth: 3 aplikasi wajib buat mahasiswa"
                className="w-full resize-none block bg-transparent border-0 px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 focus:outline-none focus:ring-0 max-h-40 overflow-y-auto leading-relaxed"
              />
            </div>
            <button
              onClick={recreate}
              disabled={recreating || !transcript.trim() || !topic.trim()}
              data-testid="recreate-btn"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-3.5 mt-4 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sk-brandDark disabled:opacity-60 disabled:hover:translate-y-0 shadow-lg shadow-sk-brand/20"
            >
              {recreating ? <><RefreshCw className="h-4 w-4 animate-spin" /> Meniru gaya & meracik ulang...</> : <><Repeat className="h-4 w-4" /> Recreate</>}
            </button>
          </div>
        </div>

        {/* Right: result */}
        <div className={`rounded-2xl border bg-sk-surface p-6 sm:p-8 shadow-sm lg:sticky lg:top-6 self-start ${recreating ? "sk-generating" : "border-sk-line"}`}>
          <div className="flex items-center justify-between mb-4 gap-2">
            <h2 className="font-extrabold text-lg text-sk-cream tracking-tight">Hasil Recreate</h2>
          </div>
          {draft ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {editing ? (
                <textarea data-testid="recreate-draft-edit" value={draft} onChange={(e) => setDraft(e.target.value)} rows={14} className="w-full rounded-xl bg-sk-bg border border-sk-brand px-4 py-3 text-sm text-sk-cream resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand leading-relaxed" />
              ) : (
                <div data-testid="recreate-draft-preview" className="rounded-xl bg-sk-bg border border-sk-line p-4 text-sm text-sk-cream whitespace-pre-wrap leading-relaxed max-h-[55vh] overflow-y-auto">{draft}</div>
              )}
              <DraftActions
                className="mt-4"
                editing={editing}
                onToggleEdit={() => (editing ? finishEdit() : setEditing(true))}
                onCopy={copySave}
                copied={copied}
                onDelete={deleteDraft}
                copyLabel="Salin & Simpan"
                editTestid="recreate-edit-btn"
                copyTestid="recreate-copy-btn"
                deleteTestid="recreate-delete-btn"
              />
              <button onClick={() => onSendToCalendar(draft)} data-testid="recreate-send-to-calendar-btn" className="w-full mt-2.5 flex items-center justify-center gap-2 rounded-full border border-sk-line py-2.5 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
                Kirim ke Kalender
              </button>
              <p className="text-xs text-sk-muted mt-3 flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-sk-brand" /> Otomatis tersimpan di Library Skrip</p>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-14 px-4">
              <div className="h-14 w-14 rounded-2xl bg-sk-brand/12 flex items-center justify-center mb-4">
                <Sparkles className="h-7 w-7 text-sk-brand" />
              </div>
              <h3 className="font-bold text-sk-cream">Hasil belum ada</h3>
              <p className="text-sk-muted text-sm mt-2 max-w-xs leading-relaxed">Upload file, cek transkripnya, masukin topik baru, lalu klik Recreate. Gaya penyampaian sumber bakal ditiru untuk topik kamu.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
